import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { IndicatorChartTypeEnums } from 'src/app/enum/indicator-chart-enums';
import { QuotationStateEnums, QuotationTypesEnums } from 'src/app/enum/quotation-enums';
import { IndicatorChart } from 'src/app/interfaces/custom-indicators/indicator-chart.interface';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ItemChartModel } from 'src/app/models/charts/global/item-chart.model';
import { QuotationChartModel } from 'src/app/models/charts/preliminaries/quotation-chart.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { QuotationBreakdownTableModel } from 'src/app/models/tables/preliminaries/quotation-breakdown-table.model';
import { QuotationTableModel } from 'src/app/models/tables/preliminaries/quotation-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { BreakdownService } from 'src/app/services/preliminaries/breakdown.service';
import { QuotationService } from 'src/app/services/preliminaries/quotation.service';

const QuotationIndicatorsConstants = {
  states: [
    { id: QuotationStateEnums.open, label: 'Abiertas', color: '#EF4444' },
    { id: QuotationStateEnums.closed, label: 'Cerradas', color: '#FB923C' },
    { id: QuotationStateEnums.approved, label: 'Aprobadas', color: '#14B8A6' },
    { id: QuotationStateEnums.refused, label: 'Rechazadas', color: '#3B82F6' }
  ],
  types: [
    { id: QuotationTypesEnums.rental, label: 'Alquiler' },
    { id: QuotationTypesEnums.newSale, label: 'Venta Nuevo' },
    { id: QuotationTypesEnums.usedSale, label: 'Venta Usado' },
    { id: QuotationTypesEnums.maintenance, label: 'Mantenimiento' },
    { id: QuotationTypesEnums.service, label: 'Servicios' }
  ]
};

@Component({
  selector: 'app-quotation',
  templateUrl: './quotation.component.html',
  styleUrls: ['./quotation.component.scss'],
  standalone: false
})
export class QuotationComponent implements OnInit {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<QuotationBreakdownTableModel> = new PagedResponseModel<QuotationBreakdownTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<QuotationTableModel> = new PagedResponseModel<QuotationTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  indicatorCharts: IndicatorChart[] = this.buildIndicatorCharts(new QuotationChartModel());
  indicatorsRequestId: number = 0;
  limit: number = 10;
  listObservers: Subscription[] = [];
  loadingIndicators: boolean = false;
  nameWindow: string = 'Cotizaciones';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  quotaionId: number = 0;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  constructor(private quotationService: QuotationService,
    private breakdownService: BreakdownService,
    private headerService: HeaderService,
    private router: Router,
    private title: Title) {
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('quotation');
    filters ? this.filters = JSON.parse(filters) : [];
    this.getIndicatorsForGeneralConsultation();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllQuotationsWithPagination(),
    ]);
  }

  async ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
  }

  buildIndicatorCharts(indicators: QuotationChartModel): IndicatorChart[] {
    const { states, types } = QuotationIndicatorsConstants;
    return [
      {
        type: IndicatorChartTypeEnums.donut,
        title: 'Cotizaciones por estado',
        labels: states.map(state => state.label),
        values: states.map(state => this.getIndicatorQuantity(indicators.states, state.id)),
        colors: states.map(state => state.color),
        total: indicators.total
      },
      {
        type: IndicatorChartTypeEnums.bar,
        title: 'Cotizaciones por tipo',
        labels: types.map(type => type.label),
        values: types.map(type => this.getIndicatorQuantity(indicators.types, type.id))
      }
    ];
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.breakdownService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Cotizacion Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las cotizaciones detalle.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.quotationService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Cotizaciones.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar cotizaciones.', error);
    }
  }

  async getAllQuotationsWithPagination() {
    try {
      this.generalData = await firstValueFrom(this.quotationService.getAllQuotationsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las cotizaciones.', error);
    }
  }

  async getAllQuotationBreakdownsWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.breakdownService.getAllBreakdownsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los detalles de las cotizaciones.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.breakdownService.getColumnsForDetailConsultationAsync());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.quotationService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getIndicatorsForGeneralConsultation() {
    // Descarta respuestas de solicitudes anteriores que lleguen después de la última
    const requestId = ++this.indicatorsRequestId;
    this.loadingIndicators = true;
    try {
      const indicators = await firstValueFrom(this.quotationService.getIndicatorsForGeneralConsultation(this.filters));
      if (requestId === this.indicatorsRequestId) {
        this.indicatorCharts = this.buildIndicatorCharts(indicators);
      }
    } catch (error) {
      console.error('Error al buscar los indicadores de las cotizaciones.', error);
    } finally {
      if (requestId === this.indicatorsRequestId)
        this.loadingIndicators = false;
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        this.getIndicatorsForGeneralConsultation();
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllQuotationsWithPagination(),
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllQuotationBreakdownsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newQuotation() {
    this.router.navigate(['/quotation/register']);
  }

  onAction(event: { action: string, item?: QuotationTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/quotation/register/${event.item.id}`]);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllQuotationsWithPagination();
        break;
      case 1:
        await this.getAllQuotationBreakdownsWithPagination();
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  // El backend no envía los estados o tipos sin cotizaciones
  private getIndicatorQuantity(items: ItemChartModel[], id: number): number {
    return items?.find(item => item.id === id)?.quantity ?? 0;
  }

  private removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  private processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

}
